import React from 'react';
import { useData } from '../../context/DataContext';
import { exportStudentsToCSV } from '../../utils/exportUtils';

export const TierDrilldownModal = ({ filter, onClose, onViewReport }) => {
  const { currentBranchFilter, getScopedStudents } = useData();

  if (!filter) return null;

  const scoped = getScopedStudents();

  const getFilteredStudents = () => {
    // 1. If filter is a tier string ('all' | 'competent' | 'bridge' | 'remediation')
    if (typeof filter === 'string') {
      return scoped.filter(s => {
        if (!s.participated) return false;
        if (filter === 'all') return true;

        const target = s.registeredGrade;
        // const target = Math.min(7, s.registeredGrade);
        const engGap = target - s.englishMastery;
        const mathGap = target - s.mathMastery;
        const isRemediation = engGap >= 3 || mathGap >= 3;
        const isCompetent = engGap <= 0 && mathGap <= 0;

        if (filter === 'remediation') return isRemediation;
        if (filter === 'competent') return isCompetent;
        if (filter === 'bridge') return !isRemediation && !isCompetent;
        return false;
      }).sort((a, b) => a.name.localeCompare(b.name));
    }

    // 2. If filter is a gap matrix object ({ grade, gapType, subject, school, section })
    const { grade, gapType, subject, school, section } = filter;
    const subjectFilter = subject || 'math';

    return scoped.filter(s => {
      if (!s.participated || s.registeredGrade !== grade) return false;
      if (school && s.school !== school) return false;
      if (section && (s.section || 'Unassigned') !== section) return false;

      const mastery = Math.floor(subjectFilter === 'math' ? s.mathMastery : s.englishMastery);
      const target = s.registeredGrade;
      // const target = Math.min(7, s.registeredGrade);
      const gap = target - mastery;

      if (gapType === 'At Grade Level') return gap <= 0;
      if (gapType === '1 Yr Below') return gap === 1;
      if (gapType === '2 Yrs Below') return gap === 2;
      if (gapType === '3 Yrs Below') return gap === 3;
      if (gapType === '4 Yrs Below') return gap >= 4;
      return false;
    }).sort((a, b) => a.name.localeCompare(b.name));
  };

  const students = getFilteredStudents();

  const getTitle = () => {
    if (typeof filter === 'string') {
      const scopeLabel = currentBranchFilter === 'all' ? 'All Branches' : currentBranchFilter;
      const tierLabels = {
        all: 'All Assessed Students',
        competent: 'At Grade Level (Competent)',
        bridge: 'Bridge the Gap',
        remediation: 'Remediation Need (FLN)'
      };
      return `${scopeLabel} — ${tierLabels[filter] || 'Students'}`;
    }

    const { grade, gapType, subject, school, section } = filter;
    const scopeLabel = section
      ? `${school} — Grade ${grade} - Section ${section}`
      : school
      ? `${school} — Grade ${grade}`
      : `Grade ${grade}`;
    return `${scopeLabel} - ${gapType} (${(subject || 'MATH').toUpperCase()})`;
  };

  const getMasteryColor = (level, grade) => {
    const target = grade;
    // const target = Math.min(7, grade);
    const gap = target - level;
    if (gap <= 0) return '#2a6a4a';
    if (gap <= 2) return '#d4870a';
    return '#dc3545';
  };

  const handleExport = () => {
    exportStudentsToCSV(students, `Filtered_Students_${typeof filter === 'string' ? filter : filter.grade}`);
  };

  return (
    <div className="mt-8 pt-8 border-t-2 border-gray-100 select-none">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h3 className="text-2xl font-bold text-gray-800">{getTitle()}</h3>
          <p className="text-sm text-gray-500">Showing detailed performance for selected criteria</p>
        </div>
        <div className="flex gap-4">
          <button
            onClick={handleExport}
            disabled={students.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-bold transition-colors cursor-pointer disabled:opacity-50"
          >
            📥 Export Excel
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 font-bold transition-colors cursor-pointer"
          >
            ✕ Close List
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 max-h-[460px] overflow-y-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 bg-blue-50 z-10">
            <tr className="bg-blue-50 text-blue-800 border-b border-gray-200">
              <th className="py-4 px-4 text-left border-b font-bold">Student ID</th>
              <th className="py-4 px-4 text-left border-b font-bold">Name</th>
              <th className="py-4 px-4 text-center border-b font-bold">Grade</th>
              <th className="py-4 px-4 text-center border-b font-bold">English Mastery</th>
              <th className="py-4 px-4 text-center border-b font-bold">Math Mastery</th>
              <th className="py-4 px-4 text-center border-b font-bold">Action</th>
            </tr>
          </thead>
          <tbody>
            {students.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-20 text-gray-400">
                  <div className="flex flex-col items-center">
                    <span className="text-6xl mb-4">🔍</span>
                    <p className="text-lg font-bold">No students found matching this criteria.</p>
                  </div>
                </td>
              </tr>
            ) : (
              students.map((student) => {
                const engColor = getMasteryColor(student.englishMastery, student.registeredGrade);
                const mathColor = getMasteryColor(student.mathMastery, student.registeredGrade);

                return (
                  <tr
                    key={student.id}
                    className="hover:bg-gray-50 border-b border-gray-100 transition-colors cursor-pointer"
                    onClick={() => onViewReport(student)}
                  >
                    <td className="py-4 px-4 font-bold text-blue-600">{student.id}</td>
                    <td className="py-4 px-4 font-semibold text-gray-800">{student.name}</td>
                    <td className="py-4 px-4 text-center font-semibold text-gray-700">
                      Grade {student.registeredGrade}
                    </td>
                    <td className="py-4 px-4 text-center font-bold" style={{ color: engColor }}>
                      {student.englishMastery}
                    </td>
                    <td className="py-4 px-4 text-center font-bold" style={{ color: mathColor }}>
                      {student.mathMastery}
                    </td>
                    <td className="py-4 px-4 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewReport(student);
                        }}
                        className="text-xs bg-blue-50 text-blue-600 px-3 py-1.5 rounded border border-blue-200 hover:bg-blue-100 font-bold transition-colors cursor-pointer"
                      >
                        View Report
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
export default TierDrilldownModal;
